/* Mocked in-memory implementation of ProductDocumentsClient for local development and UI wiring. */

import { IProductDocumentsClient } from './IProductDocumentsClient';
import { ProductDocumentModel, FileResponse } from './ofdTypes';

/* ---- Date helpers (keeps mock data evergreen) ---- */

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

function daysFromNow(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString();
}

/* ---- Mock Data ---- */

let mockProductDocuments: ProductDocumentModel[] = [
  {
    id: 1,
    productId: 101,
    documentId: 1,
    fileName: 'product_spec_coffee_beans.pdf',
    altDocName: 'Coffee Beans - Product Specification',
    fileType: 'application/pdf',
    effectiveStartDate: daysAgo(180),
    effectiveEndDate: daysFromNow(180),
  },
  {
    id: 2,
    productId: 101,
    documentId: 2,
    fileName: 'coffee_beans_nutritional.pdf',
    altDocName: 'Coffee Beans - Nutritional Information',
    fileType: 'application/pdf',
    effectiveStartDate: daysAgo(180),
  },
  {
    id: 3,
    productId: 102,
    documentId: 3,
    fileName: 'tea_blend_certificate.pdf',
    altDocName: 'Premium Tea Blend - Certificate of Origin',
    fileType: 'application/pdf',
    effectiveStartDate: daysAgo(90),
    effectiveEndDate: daysFromNow(270),
  },
  {
    id: 4,
    productId: 103,
    documentId: 4,
    fileName: 'syrup_allergen_info.pdf',
    altDocName: 'Vanilla Syrup - Allergen Information',
    fileType: 'application/pdf',
    effectiveStartDate: daysAgo(180),
  },
  {
    id: 5,
    productId: 104,
    documentId: 5,
    fileName: 'milk_product_sheet.pdf',
    altDocName: 'Oat Milk - Product Data Sheet',
    fileType: 'application/pdf',
    effectiveStartDate: daysAgo(120),
    effectiveEndDate: daysFromNow(240),
  },
  {
    id: 6,
    productId: 105,
    documentId: 6,
    fileName: 'cups_sustainability_report.pdf',
    altDocName: 'Takeaway Cups - Sustainability Report',
    fileType: 'application/pdf',
    effectiveStartDate: daysAgo(180),
  },
];

const mockFileContents: Record<number, string> = {
  1: 'Mock PDF content: Coffee Beans Product Specification - Origin: Colombia, Roast: Medium, Grade: AA',
  2: 'Mock PDF content: Nutritional Information - Energy: 5kcal/100ml, Protein: 0.3g, Carbs: 0g',
  3: 'Mock PDF content: Certificate of Origin - Product certified organic, sourced from Sri Lanka',
  4: 'Mock PDF content: Allergen Information - Contains: None. May contain traces of nuts.',
  5: 'Mock PDF content: Oat Milk Data Sheet - 100% plant-based, barista edition, low sugar',
  6: 'Mock PDF content: Sustainability Report - 100% recyclable materials, carbon neutral production',
};

let nextDocumentId = 7;

/* ---- Mocked Client Implementation ---- */

export class ProductDocumentsClientMocked implements IProductDocumentsClient {
  /**
   * Upload a product document (mocked - stores in memory)
   */
  async upload(body?: ProductDocumentModel): Promise<void> {
    await this.delay(400);

    if (!body) {
      throw new Error('Document data is required');
    }

    const newDoc: ProductDocumentModel = {
      ...body,
      id: nextDocumentId++,
      documentId: body.documentId || nextDocumentId,
    };

    mockProductDocuments.push(newDoc);
    console.log('[ProductDocumentsClientMocked] Uploaded document:', newDoc.fileName);

    return Promise.resolve();
  }

  /**
   * Download a product document (mocked - returns fake blob)
   */
  async download(documentId?: number): Promise<FileResponse> {
    await this.delay(300);

    const doc = mockProductDocuments.find((d) => d.documentId === documentId || d.id === documentId);

    if (!doc) {
      // Return a generic document if not found
      const genericContent = `Mock document content for document ID ${documentId}`;
      return {
        data: new Blob([genericContent], { type: 'application/pdf' }),
        status: 200,
        fileName: `document_${documentId}.pdf`,
        headers: {
          'content-type': 'application/pdf',
          'content-disposition': `attachment; filename="document_${documentId}.pdf"`,
        },
      };
    }

    const content = mockFileContents[doc.documentId || doc.id!] || `Mock content for ${doc.fileName}`;
    const blob = new Blob([content], { type: doc.fileType || 'application/pdf' });

    console.log('[ProductDocumentsClientMocked] Downloaded document:', doc.fileName);

    return {
      data: blob,
      status: 200,
      fileName: doc.fileName,
      headers: {
        'content-type': doc.fileType || 'application/pdf',
        'content-disposition': `attachment; filename="${doc.fileName}"`,
      },
    };
  }

  /**
   * Delete a product document (mocked - removes from memory)
   */
  async deleteDocument(id: number): Promise<void> {
    await this.delay(250);

    const index = mockProductDocuments.findIndex((d) => d.id === id || d.documentId === id);

    if (index >= 0) {
      const deleted = mockProductDocuments.splice(index, 1)[0];
      console.log('[ProductDocumentsClientMocked] Deleted document:', deleted.fileName);
    } else {
      console.log('[ProductDocumentsClientMocked] Document not found:', id);
    }

    return Promise.resolve();
  }

  /* ---- Helper Methods ---- */

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /* ---- Test Utilities ---- */

  /**
   * Get all product documents (for testing)
   */
  getAllDocuments(): ProductDocumentModel[] {
    return [...mockProductDocuments];
  }

  /**
   * Get documents by product ID (for testing)
   */
  getDocumentsByProductId(productId: number): ProductDocumentModel[] {
    return mockProductDocuments.filter((d) => d.productId === productId);
  }

  /**
   * Reset mock data to initial state (for testing)
   */
  resetMockData(): void {
    mockProductDocuments = [
      {
        id: 1,
        productId: 101,
        documentId: 1,
        fileName: 'product_spec_coffee_beans.pdf',
        altDocName: 'Coffee Beans - Product Specification',
        fileType: 'application/pdf',
        effectiveStartDate: daysAgo(180),
        effectiveEndDate: daysFromNow(180),
      },
      {
        id: 2,
        productId: 101,
        documentId: 2,
        fileName: 'coffee_beans_nutritional.pdf',
        altDocName: 'Coffee Beans - Nutritional Information',
        fileType: 'application/pdf',
        effectiveStartDate: daysAgo(180),
      },
      {
        id: 3,
        productId: 102,
        documentId: 3,
        fileName: 'tea_blend_certificate.pdf',
        altDocName: 'Premium Tea Blend - Certificate of Origin',
        fileType: 'application/pdf',
        effectiveStartDate: daysAgo(90),
        effectiveEndDate: daysFromNow(270),
      },
      {
        id: 4,
        productId: 103,
        documentId: 4,
        fileName: 'syrup_allergen_info.pdf',
        altDocName: 'Vanilla Syrup - Allergen Information',
        fileType: 'application/pdf',
        effectiveStartDate: daysAgo(180),
      },
      {
        id: 5,
        productId: 104,
        documentId: 5,
        fileName: 'milk_product_sheet.pdf',
        altDocName: 'Oat Milk - Product Data Sheet',
        fileType: 'application/pdf',
        effectiveStartDate: daysAgo(120),
        effectiveEndDate: daysFromNow(240),
      },
      {
        id: 6,
        productId: 105,
        documentId: 6,
        fileName: 'cups_sustainability_report.pdf',
        altDocName: 'Takeaway Cups - Sustainability Report',
        fileType: 'application/pdf',
        effectiveStartDate: daysAgo(180),
      },
    ];
    nextDocumentId = 7;
    console.log('[ProductDocumentsClientMocked] Mock data reset');
  }
}
